import React, { createContext, useContext, useState, useCallback } from "react";

interface ILoaderContext {
  loaderState: LoaderInfo;
  showLoader: (message?: string, size?: "small" | "default" | "large") => void;
  hideLoader: () => void;
}

const LoaderContext = createContext<ILoaderContext>(null!);

interface LoaderProviderProps {
  children?: React.ReactNode;
}

interface LoaderInfo {
  showLoader: boolean;
  size?: "small" | "default" | "large";
  message?: string;
}

export const LoaderProvider = ({ children }: LoaderProviderProps) => {
  const [loaderState, setLoaderState] = useState<LoaderInfo>({
    showLoader: false,
    message: "",
    size: "large",
  });

  const showLoader = useCallback(
    (message?: string, size: "small" | "default" | "large" = "large") => {
      setLoaderState({
        showLoader: true,
        message,
        size,
      });
    },
    []
  );

  const hideLoader = useCallback(() => {
    setLoaderState({
      showLoader: false,
      message: "",
    });
  }, []);

  const value = React.useMemo(
    () => ({ loaderState, showLoader, hideLoader }),
    [loaderState, showLoader, hideLoader]
  );

  return (
    <LoaderContext.Provider value={value}>{children}</LoaderContext.Provider>
  );
};

export const useLoaderProvider = () => {
  const context = useContext(LoaderContext);
  if (context === null) {
    throw new Error("LoaderContext must be used within a LoaderProvider");
  }
  return context;
};
